import i18next from 'i18next';

let isBound = false;

const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

const padDatePart = (value) => String(value).padStart(2, '0');

const getDatePickerLocale = () => {
    const language = i18next.language || document.documentElement.lang || 'uk';
    return language || 'uk';
};

const toLocalDateTimeValue = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return '';
    }
    return [
        date.getFullYear(),
        padDatePart(date.getMonth() + 1),
        padDatePart(date.getDate()),
    ].join('-') + `T${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
};

const toLocalDateValue = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return '';
    }
    return [
        date.getFullYear(),
        padDatePart(date.getMonth() + 1),
        padDatePart(date.getDate()),
    ].join('-');
};

const parseLocalDateTimeValue = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (!match) {
        return null;
    }
    const [, year, month, day, hour, minute] = match.map(Number);
    const date = new Date(year, month - 1, day, hour, minute, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
};

const parseLocalDateValue = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
        return null;
    }
    const [, year, month, day] = match.map(Number);
    const date = new Date(year, month - 1, day, 0, 0, 0, 0);
    return Number.isNaN(date.getTime()) ? null : date;
};

const getPickerLabel = (picker, key, fallback) => picker.data(key) || fallback;

const getDatePickerMode = (picker) => (picker.data('mode') === 'date' ? 'date' : 'datetime');

const isPastDisabled = (picker) => (
    getDatePickerMode(picker) === 'datetime'
    && String(picker.data('disable-past') || '').toLowerCase() === 'true'
);

const getDefaultDatePickerDate = () => {
    const now = new Date();
    const date = new Date();
    date.setSeconds(0, 0);
    date.setMinutes(Math.ceil(date.getMinutes() / 5) * 5);
    if (date <= now) {
        date.setMinutes(date.getMinutes() + 5);
    }
    return date;
};

const getTodayDatePickerDate = () => {
    const date = new Date();
    date.setSeconds(0, 0);
    return date;
};

const getMinimumDatePickerDate = (picker) => (isPastDisabled(picker) ? getDefaultDatePickerDate() : null);

const getDateOnlyTime = (date) => new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0,
).getTime();

const clampDateTimePickerDate = (picker, date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return null;
    }
    const minimumDate = getMinimumDatePickerDate(picker);
    if (minimumDate && date < minimumDate) {
        return new Date(minimumDate);
    }
    return date;
};

const setDateTimePickerTime = (picker, {hour, minute, hourDelta = 0, minuteDelta = 0}) => {
    const state = getDatePickerState(picker);
    const nextDate = new Date(state.selectedDate);
    if (Number.isFinite(hour)) {
        nextDate.setHours(Math.min(23, Math.max(0, hour)));
    }
    if (Number.isFinite(minute)) {
        nextDate.setMinutes(Math.min(59, Math.max(0, minute)));
    }
    if (hourDelta) {
        nextDate.setHours(nextDate.getHours() + hourDelta);
    }
    if (minuteDelta) {
        nextDate.setMinutes(nextDate.getMinutes() + minuteDelta);
    }
    nextDate.setSeconds(0, 0);
    const clampedDate = clampDateTimePickerDate(picker, nextDate) || nextDate;
    setDateTimePickerValue(picker.find('[data-date-time-picker-value]'), clampedDate, false);
    picker.find('[data-date-time-picker-hour]').val(padDatePart(clampedDate.getHours()));
    picker.find('[data-date-time-picker-minute]').val(padDatePart(clampedDate.getMinutes()));
};

const formatDatePickerLabel = (picker, date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
        return getPickerLabel(picker, 'placeholder', 'Select date and time');
    }
    try {
        const mode = getDatePickerMode(picker);
        return new Intl.DateTimeFormat(getDatePickerLocale(), {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            ...(mode === 'date' ? {} : {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }),
        }).format(date);
    } catch (error) {
        if (getDatePickerMode(picker) === 'date') {
            return `${padDatePart(date.getDate())}.${padDatePart(date.getMonth() + 1)}.${date.getFullYear()}`;
        }
        return `${padDatePart(date.getDate())}.${padDatePart(date.getMonth() + 1)}.${date.getFullYear()} ${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}`;
    }
};

const getDatePickerState = (picker) => {
    const input = picker.find('[data-date-time-picker-value]');
    const selectedDate = (
        getDatePickerMode(picker) === 'date'
            ? parseLocalDateValue(input.val())
            : parseLocalDateTimeValue(input.val())
    ) || (getDatePickerMode(picker) === 'date' ? getTodayDatePickerDate() : getDefaultDatePickerDate());
    const viewYear = Number(picker.data('view-year'));
    const viewMonth = Number(picker.data('view-month'));
    return {
        selectedDate,
        viewYear: Number.isInteger(viewYear) ? viewYear : selectedDate.getFullYear(),
        viewMonth: Number.isInteger(viewMonth) ? viewMonth : selectedDate.getMonth(),
    };
};

const getLocalizedWeekdays = () => {
    const locale = getDatePickerLocale();
    const monday = new Date(2026, 4, 4);
    return Array.from({length: 7}, (_, index) => {
        const day = new Date(monday);
        day.setDate(monday.getDate() + index);
        try {
            return new Intl.DateTimeFormat(locale, {weekday: 'short'}).format(day);
        } catch (error) {
            return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'][index];
        }
    });
};

const isSameDate = (left, right) => (
    left instanceof Date
    && right instanceof Date
    && left.getFullYear() === right.getFullYear()
    && left.getMonth() === right.getMonth()
    && left.getDate() === right.getDate()
);

const updateDateTimePickerPlacement = (picker) => {
    const trigger = picker.find('[data-date-time-picker-trigger]').get(0);
    const popover = picker.find('[data-date-time-picker-popover]').get(0);
    const pickerElement = picker.get(0);
    if (!trigger || !popover || !pickerElement) {
        return;
    }
    const gap = 8;
    const viewportPadding = 12;
    const useFixedPopover = String(picker.attr('data-date-time-picker-fixed') || '') === 'true';
    const triggerRect = trigger.getBoundingClientRect();
    const pickerRect = pickerElement.getBoundingClientRect();
    const spaceAbove = Math.max(0, triggerRect.top - viewportPadding - gap);
    const spaceBelow = Math.max(0, window.innerHeight - triggerRect.bottom - viewportPadding - gap);
    const availableWidth = Math.max(220, window.innerWidth - (viewportPadding * 2));
    const previousDirection = picker.hasClass('is-above') ? 'above' : 'below';

    picker.removeClass('is-above is-below').addClass('is-measuring is-below');
    popover.style.maxHeight = '';
    popover.style.maxWidth = `${availableWidth}px`;
    popover.style.left = '';
    popover.style.right = '';
    popover.style.top = '';
    popover.style.bottom = '';
    popover.style.position = '';
    popover.style.overflow = '';
    const popoverHeight = popover.offsetHeight || 0;
    const popoverWidth = Math.min(popover.offsetWidth || availableWidth, availableWidth);
    picker.removeClass('is-measuring is-below');

    let direction = 'below';
    if (popoverHeight > spaceBelow && spaceAbove > spaceBelow) {
        direction = 'above';
    } else if (previousDirection === 'above' && spaceAbove >= Math.min(popoverHeight, spaceBelow)) {
        direction = 'above';
    }

    const availableSpace = direction === 'above' ? spaceAbove : spaceBelow;
    picker
        .toggleClass('is-above', direction === 'above')
        .toggleClass('is-below', direction !== 'above');
    const preferredLeft = triggerRect.right - popoverWidth;
    const minLeft = viewportPadding;
    const maxLeft = Math.max(minLeft, window.innerWidth - viewportPadding - popoverWidth);
    const clampedLeft = Math.min(Math.max(preferredLeft, minLeft), maxLeft);

    if (useFixedPopover) {
        const desiredTop = direction === 'above'
            ? triggerRect.top - gap - popoverHeight
            : triggerRect.bottom + gap;
        const minTop = viewportPadding;
        const maxTop = Math.max(minTop, window.innerHeight - viewportPadding - popoverHeight);
        const clampedTop = Math.min(Math.max(desiredTop, minTop), maxTop);
        const maxHeight = Math.max(220, window.innerHeight - (viewportPadding * 2));
        const needsVerticalScroll = popoverHeight > maxHeight;

        popover.style.position = 'fixed';
        popover.style.top = `${Math.round(clampedTop)}px`;
        popover.style.bottom = 'auto';
        popover.style.left = `${Math.round(clampedLeft)}px`;
        popover.style.right = 'auto';
        popover.style.maxHeight = needsVerticalScroll ? `${maxHeight}px` : 'none';
        popover.style.overflow = needsVerticalScroll ? 'auto' : 'visible';
        return;
    }

    popover.style.left = `${Math.round(clampedLeft - pickerRect.left)}px`;
    popover.style.right = 'auto';
    popover.style.maxHeight = `${Math.max(220, availableSpace)}px`;
};

const renderDateTimePicker = (picker) => {
    const state = getDatePickerState(picker);
    const mode = getDatePickerMode(picker);
    picker.data('view-year', state.viewYear);
    picker.data('view-month', state.viewMonth);

    const firstDay = new Date(state.viewYear, state.viewMonth, 1);
    const firstWeekday = (firstDay.getDay() + 6) % 7;
    const gridStart = new Date(state.viewYear, state.viewMonth, 1 - firstWeekday);
    const monthLabel = (() => {
        try {
            return new Intl.DateTimeFormat(getDatePickerLocale(), {month: 'long', year: 'numeric'}).format(firstDay);
        } catch (error) {
            return `${state.viewYear}-${padDatePart(state.viewMonth + 1)}`;
        }
    })();
    const weekdays = getLocalizedWeekdays()
        .map((weekday) => `<div class="ogo-date-time-picker__weekday">${escapeHtml(weekday)}</div>`)
        .join('');
    const today = new Date();
    const minimumDate = getMinimumDatePickerDate(picker);
    const minimumDayTime = minimumDate ? getDateOnlyTime(minimumDate) : null;
    const days = Array.from({length: 42}, (_, index) => {
        const date = new Date(gridStart);
        date.setDate(gridStart.getDate() + index);
        const selectedDate = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            state.selectedDate.getHours(),
            state.selectedDate.getMinutes(),
            0,
            0,
        );
        const dateValue = mode === 'date'
            ? toLocalDateValue(selectedDate)
            : toLocalDateTimeValue(selectedDate);
        const isDisabled = minimumDayTime !== null && getDateOnlyTime(date) < minimumDayTime;
        return `
            <button
                type="button"
                class="ogo-date-time-picker__day ${date.getMonth() !== state.viewMonth ? 'is-muted' : ''} ${isSameDate(date, state.selectedDate) ? 'is-selected' : ''} ${isSameDate(date, today) ? 'is-today' : ''} ${isDisabled ? 'is-disabled' : ''}"
                data-date-time-picker-day="${dateValue}"
                ${isDisabled ? 'disabled aria-disabled="true"' : ''}
            >
                ${date.getDate()}
            </button>`;
    }).join('');

    picker.find('[data-date-time-picker-calendar]').html(`
        <div class="ogo-date-time-picker__header">
            <button type="button" class="ogo-date-time-picker__nav" data-date-time-picker-prev aria-label="${escapeHtml(getPickerLabel(picker, 'previousMonth', 'Previous month'))}">‹</button>
            <div class="ogo-date-time-picker__month">${escapeHtml(monthLabel)}</div>
            <button type="button" class="ogo-date-time-picker__nav" data-date-time-picker-next aria-label="${escapeHtml(getPickerLabel(picker, 'nextMonth', 'Next month'))}">›</button>
        </div>
        <div class="ogo-date-time-picker__grid ogo-date-time-picker__grid--weekdays">${weekdays}</div>
        <div class="ogo-date-time-picker__grid">${days}</div>
        ${mode === 'date' ? '' : `<div class="ogo-date-time-picker__time-row">
            <label class="ogo-date-time-picker__time-label">${escapeHtml(getPickerLabel(picker, 'time', 'Time'))}</label>
            <div class="ogo-date-time-picker__time-control">
                <button type="button" class="ogo-date-time-picker__time-step" data-date-time-picker-time-step data-unit="hour" data-step="-1" aria-label="${escapeHtml(getPickerLabel(picker, 'decreaseHour', 'Decrease hour'))}">−</button>
                <input class="ogo-date-time-picker__time-input" type="number" min="0" max="23" step="1" value="${padDatePart(state.selectedDate.getHours())}" data-date-time-picker-hour aria-label="${escapeHtml(getPickerLabel(picker, 'hour', 'Hour'))}">
                <button type="button" class="ogo-date-time-picker__time-step" data-date-time-picker-time-step data-unit="hour" data-step="1" aria-label="${escapeHtml(getPickerLabel(picker, 'increaseHour', 'Increase hour'))}">+</button>
            </div>
            <span class="ogo-date-time-picker__time-separator">:</span>
            <div class="ogo-date-time-picker__time-control">
                <button type="button" class="ogo-date-time-picker__time-step" data-date-time-picker-time-step data-unit="minute" data-step="-5" aria-label="${escapeHtml(getPickerLabel(picker, 'decreaseMinute', 'Decrease minute'))}">−</button>
                <input class="ogo-date-time-picker__time-input" type="number" min="0" max="59" step="5" value="${padDatePart(state.selectedDate.getMinutes())}" data-date-time-picker-minute aria-label="${escapeHtml(getPickerLabel(picker, 'minute', 'Minute'))}">
                <button type="button" class="ogo-date-time-picker__time-step" data-date-time-picker-time-step data-unit="minute" data-step="5" aria-label="${escapeHtml(getPickerLabel(picker, 'increaseMinute', 'Increase minute'))}">+</button>
            </div>
        </div>`}
        <div class="ogo-date-time-picker__actions">
            <button type="button" class="ogo-date-time-picker__action" data-date-time-picker-clear>${escapeHtml(getPickerLabel(picker, 'clear', 'Clear'))}</button>
            <button type="button" class="ogo-date-time-picker__action" data-date-time-picker-today>${escapeHtml(getPickerLabel(picker, 'today', 'Today'))}</button>
            <button type="button" class="ogo-date-time-picker__action ogo-date-time-picker__action--primary" data-date-time-picker-done>${escapeHtml(getPickerLabel(picker, 'done', 'Done'))}</button>
        </div>
    `);
    if (picker.hasClass('is-open')) {
        updateDateTimePickerPlacement(picker);
    }
};

export const dateTimePickerHtml = ({
    id,
    label,
    placeholder,
    className = '',
    inputClass = '',
    mode = 'datetime',
    disablePast = false,
    fixed = false,
    labels = {},
}) => `
    <div class="ogo-field ${className}">
        <label class="ogo-field-label" for="${id}-trigger">${escapeHtml(label)}</label>
        <div
            class="ogo-date-time-picker"
            data-date-time-picker
            data-mode="${escapeHtml(mode === 'date' ? 'date' : 'datetime')}"
            data-disable-past="${disablePast ? 'true' : 'false'}"
            data-date-time-picker-fixed="${fixed ? 'true' : 'false'}"
            data-placeholder="${escapeHtml(placeholder)}"
            data-today="${escapeHtml(labels.today || 'Today')}"
            data-clear="${escapeHtml(labels.clear || 'Clear')}"
            data-done="${escapeHtml(labels.done || 'Done')}"
            data-previous-month="${escapeHtml(labels.previousMonth || 'Previous month')}"
            data-next-month="${escapeHtml(labels.nextMonth || 'Next month')}"
            data-time="${escapeHtml(labels.time || 'Time')}"
            data-hour="${escapeHtml(labels.hour || 'Hour')}"
            data-minute="${escapeHtml(labels.minute || 'Minute')}"
            data-decrease-hour="${escapeHtml(labels.decreaseHour || 'Decrease hour')}"
            data-increase-hour="${escapeHtml(labels.increaseHour || 'Increase hour')}"
            data-decrease-minute="${escapeHtml(labels.decreaseMinute || 'Decrease minute')}"
            data-increase-minute="${escapeHtml(labels.increaseMinute || 'Increase minute')}"
        >
            <button
                type="button"
                id="${id}-trigger"
                class="ogo-date-time-picker__trigger"
                aria-haspopup="dialog"
                aria-expanded="false"
                data-date-time-picker-trigger
            >
                <span class="ogo-date-time-picker__trigger-label" data-date-time-picker-label>${escapeHtml(placeholder)}</span>
                <span class="ogo-date-time-picker__trigger-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" focusable="false">
                        <path d="M8 2v2h8V2h2v2h3l1 1v16l-1 1H3l-1-1V5l1-1h3V2h2Zm12 8H4v10h16V10ZM6 6H4v2h16V6h-2v1h-2V6H8v1H6V6Z" fill="currentColor"></path>
                    </svg>
                </span>
            </button>
            <input id="${id}" class="ogo-date-time-picker__value ${inputClass}" type="hidden" data-date-time-picker-value>
            <div class="invalid-feedback"></div>
            <div class="ogo-date-time-picker__popover" data-date-time-picker-popover role="dialog" aria-label="${escapeHtml(label)}">
                <div class="ogo-date-time-picker__calendar" data-date-time-picker-calendar></div>
            </div>
        </div>
    </div>`;

export const setDateTimePickerValue = (inputSelector, date, shouldRender = true) => {
    const input = $(inputSelector);
    const picker = input.closest('[data-date-time-picker]');
    const normalizedDate = date instanceof Date && !Number.isNaN(date.getTime()) ? date : null;
    const mode = getDatePickerMode(picker);
    input.val(normalizedDate ? (mode === 'date' ? toLocalDateValue(normalizedDate) : toLocalDateTimeValue(normalizedDate)) : '').trigger('change');
    picker.find('[data-date-time-picker-label]').text(formatDatePickerLabel(picker, normalizedDate));
    if (normalizedDate) {
        picker.data('view-year', normalizedDate.getFullYear());
        picker.data('view-month', normalizedDate.getMonth());
    }
    if (shouldRender) {
        renderDateTimePicker(picker);
    } else if (picker.hasClass('is-open')) {
        updateDateTimePickerPlacement(picker);
    }
};

export const isDateTimePickerValueInPast = (inputSelector) => {
    const input = $(inputSelector);
    const picker = input.closest('[data-date-time-picker]');
    if (!picker.length || !isPastDisabled(picker)) {
        return false;
    }
    const date = parseLocalDateTimeValue(input.val());
    const minimumDate = getMinimumDatePickerDate(picker);
    return Boolean(date && minimumDate && date < minimumDate);
};

export const clearDateTimePicker = (inputSelector) => {
    const input = $(inputSelector);
    const picker = input.closest('[data-date-time-picker]');
    picker.removeData('view-year').removeData('view-month');
    setDateTimePickerValue(input, null);
};

export const closeDateTimePickers = () => {
    $('[data-date-time-picker]').removeClass('is-open is-above is-below is-measuring')
        .find('[data-date-time-picker-trigger]')
        .attr('aria-expanded', 'false');
};

export const bindDateTimePickerEvents = () => {
    if (isBound) {
        return;
    }
    isBound = true;

    $('body').on('click', '[data-date-time-picker-trigger]', function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const picker = $(this).closest('[data-date-time-picker]');
        const isOpen = picker.hasClass('is-open');
        closeDateTimePickers();
        picker.toggleClass('is-open', !isOpen);
        $(this).attr('aria-expanded', isOpen ? 'false' : 'true');
        if (!isOpen) {
            renderDateTimePicker(picker);
            updateDateTimePickerPlacement(picker);
        }
    });
    $('body').on('click', '[data-date-time-picker-prev]', function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const picker = $(this).closest('[data-date-time-picker]');
        const state = getDatePickerState(picker);
        const previousMonth = new Date(state.viewYear, state.viewMonth - 1, 1);
        picker.data('view-year', previousMonth.getFullYear());
        picker.data('view-month', previousMonth.getMonth());
        renderDateTimePicker(picker);
    });
    $('body').on('click', '[data-date-time-picker-next]', function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const picker = $(this).closest('[data-date-time-picker]');
        const state = getDatePickerState(picker);
        const nextMonth = new Date(state.viewYear, state.viewMonth + 1, 1);
        picker.data('view-year', nextMonth.getFullYear());
        picker.data('view-month', nextMonth.getMonth());
        renderDateTimePicker(picker);
    });
    $('body').on('click', '[data-date-time-picker-day]', function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.disabled) {
            return;
        }
        const picker = $(this).closest('[data-date-time-picker]');
        const input = picker.find('[data-date-time-picker-value]');
        const rawValue = $(this).data('date-time-picker-day');
        const date = getDatePickerMode(picker) === 'date'
            ? parseLocalDateValue(rawValue)
            : parseLocalDateTimeValue(rawValue);
        setDateTimePickerValue(input, getDatePickerMode(picker) === 'date' ? date : clampDateTimePickerDate(picker, date));
    });
    $('body').on('input change', '[data-date-time-picker-hour], [data-date-time-picker-minute]', function (event) {
        event.stopPropagation();
        const picker = $(this).closest('[data-date-time-picker]');
        if (getDatePickerMode(picker) === 'date') {
            return;
        }
        const hour = Math.min(23, Math.max(0, Number(picker.find('[data-date-time-picker-hour]').val()) || 0));
        const minute = Math.min(59, Math.max(0, Number(picker.find('[data-date-time-picker-minute]').val()) || 0));
        setDateTimePickerTime(picker, {hour, minute});
    });
    $('body').on('click', '[data-date-time-picker-time-step]', function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const button = $(this);
        const picker = button.closest('[data-date-time-picker]');
        if (getDatePickerMode(picker) === 'date') {
            return;
        }
        const step = Number(button.data('step')) || 0;
        const unit = button.data('unit');
        setDateTimePickerTime(picker, {
            hourDelta: unit === 'hour' ? step : 0,
            minuteDelta: unit === 'minute' ? step : 0,
        });
    });
    $('body').on('click', '[data-date-time-picker-today]', function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const picker = $(this).closest('[data-date-time-picker]');
        setDateTimePickerValue(
            picker.find('[data-date-time-picker-value]'),
            getDatePickerMode(picker) === 'date' ? getTodayDatePickerDate() : getDefaultDatePickerDate(),
        );
    });
    $('body').on('click', '[data-date-time-picker-clear]', function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        clearDateTimePicker($(this).closest('[data-date-time-picker]').find('[data-date-time-picker-value]'));
    });
    $('body').on('click', '[data-date-time-picker-done]', function (event) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const picker = $(this).closest('[data-date-time-picker]');
        const input = picker.find('[data-date-time-picker-value]');
        input.trigger('date-time-picker:done', [input.val()]);
        picker.trigger('date-time-picker:done', [input.val()]);
        closeDateTimePickers();
    });
    $('body').on('click', function (event) {
        if (!$(event.target).closest('[data-date-time-picker]').length) {
            closeDateTimePickers();
        }
    });
    $(window).on('resize scroll', () => {
        $('[data-date-time-picker].is-open').each(function () {
            updateDateTimePickerPlacement($(this));
        });
    });
    $('body').on('keydown', function (event) {
        if (event.key === 'Escape') {
            closeDateTimePickers();
        }
    });
};
